import { useSceneEvents } from "@/components/common/scene-events-context";
import {
  CameraAnimation,
  CameraAnimationProps,
} from "@/components/r3f/animations/camera-animation";
import { AnnotationsRenderer } from "@/components/r3f/renderers/annotations/annotations-renderer";
import { MeasurementsRenderer } from "@/components/r3f/renderers/measurements/measurements-renderer";
import { PanoMeasureMultiWallsRenderer } from "@/components/r3f/renderers/measurements/pano-measure-multi-walls-renderer";
import { WayPointTarget } from "@/components/r3f/renderers/odometry-paths/walk-paths-renderer";
import { PanoramaRenderer } from "@/components/r3f/renderers/panorama-renderer";
import { PinMarkerRenderer } from "@/components/r3f/renderers/pin-marker/pin-marker-renderer";
import { WaypointPosition } from "@/components/r3f/renderers/waypoint-label-render";
import { selectShouldRenderBoundingBoxes } from "@/features/ai-chat/ai-selectors";
import { BoundingBoxRenderer } from "@/features/ai-chat/bounding-box-renderer";
import { useSceneContextMenuEventHandlers } from "@/hooks/use-scene-context-menu-event-handlers";
import { PanoObject } from "@/object-cache";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { selectPanoMeasurementWalls } from "@/store/measurement-tool-selector";
import { Measurement } from "@/store/measurement-tool-slice";
import { setPhotoObject } from "@/store/modes/walk-mode-slice";
import { selectPanoAnnotationSection } from "@/store/selections-selectors";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectFilteredElementsWithTags } from "@/store/tags/tags-selectors";
import { PickingToolsRef } from "@/tools/picking-tools";
import { usePanoAnnotationsAdjustedPose } from "@/utils/camera-transform";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import {
  CachedWorldTransform,
  createSignedUrlForIElementUri,
  Img360PanoRef,
  selectChildDepthFirst,
  selectChildrenDepthFirst,
  useControlsLockContext,
  useNonExhaustiveEffect,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import {
  GUID,
  IElementDepthMap,
  IElementGenericAnnotation,
  IElementGenericImgSheet,
  IElementImageCommand,
  IElementImg360,
  isIElementGenericAnnotation,
  isIElementMarkup,
} from "@faro-lotv/ielement-types";
import {
  assert,
  decodeCdiBuffer,
  EquirectangularDepthImage,
  LodPano,
} from "@faro-lotv/lotv";
import { ProjectApi, useApiClientContext } from "@faro-lotv/service-wires";
import { Camera, ThreeEvent, useThree } from "@react-three/fiber";
import { isEqual } from "es-toolkit";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { Matrix4, Plane, Quaternion, Vector3 } from "three";
import { matrix4ToAlignmentTransform } from "../alignment-modes-commons/alignment-transform";
import { WalkPlaceholders } from "./walk-placeholders";
import {
  selectDepthMapForImg360,
  selectIsPanoAligned,
  selectIsPanoWithDepth,
} from "./walk-scene-selectors";
import { WalkSceneActiveElement } from "./walk-types";

export type WalkPanoramaProps = PickingToolsRef & {
  /* The panorama object to render */
  pano: PanoObject;

  photoObject?: IElementImageCommand;

  /** List of all the placeholders to show */
  placeholders: IElementImg360[];

  /** If true other panorama's placeholders will be rendered  */
  shouldShowPlaceholders: boolean;

  /** If true, the panorama's annotations will be rendered  */
  shouldShowAnnotations: boolean;

  /** List of all the annotations for this area */
  annotations: IElementGenericAnnotation[];

  /** True if the active tool is using picking */
  isPickingToolActive: boolean;

  /** List of all the measurements in the store for the current selection */
  measurements: Measurement[];

  /** The floor image where the placeholders are placed */
  sheetForElevation?: IElementGenericImgSheet;

  /* True to show, will animate opacity */
  visible: boolean;

  /** Optional clipping planes */
  clippingPlanes?: Plane[];

  /** Element that should be highlighted, e.g. with a hover effect */
  highlightedElement?: GUID;

  /** Define to true to register this as a secondary view for split screen rendering */
  isSecondaryView?: boolean;

  /** Callback executed when the active element should be changed */
  onTargetElementChanged(element: WalkSceneActiveElement): void;

  /** Callback called when the active waypoint target element inside the walk scene has changed. */
  onWayPointChanged(target: WayPointTarget): void;

  /** Callback called when the list of displayed waypoints has changed. */
  onRenderedWaypointsChanged(newWaypoints: WaypointPosition[]): void;

  /** Callback executed, when the rendered transform of the pano changed */
  onPanoTransformChanged?(transform: CachedWorldTransform): void;
};

/**
 *
 * @param pano The pano image to load depth for
 * @param depthMap the depth map iElement
 * @param projectApi project API client class
 * @param signal Object communicating whether the depth loading was aborted
 * @returns A promise that resolves when the depth data are loaded
 */
export async function loadPanoDepthData(
  pano: LodPano,
  depthMap: IElementDepthMap,
  projectApi: ProjectApi,
  signal: AbortSignal,
): Promise<void> {
  const signedUrl = await createSignedUrlForIElementUri(
    projectApi,
    depthMap,
  ).requestSignedUrl();

  const ret = await fetch(signedUrl, { signal });
  if (!ret.ok) return;
  const b = await ret.blob();
  const data = await b.arrayBuffer();

  let depthData: Float32Array;
  try {
    depthData = decodeCdiBuffer(new DataView(data));
  } catch {
    depthData = new Float32Array(data);
  }

  const equirectImage = new EquirectangularDepthImage(
    depthMap.pixelWidth,
    depthData,
  );
  // Increase the offset to make the normal more stable while moving the mouse over the pano.
  // By experiments with multiple datasets, 9px is chosen as a good value results stable normal.
  equirectImage.normalComputeTrianglePointsOffset = 9;
  if (!signal.aborted) {
    pano.tiledPano.setDepths(equirectImage);
  }
}

type PanoWidthDepthProps = PickingToolsRef &
  Pick<
    WalkPanoramaProps,
    "pano" | "sheetForElevation" | "isSecondaryView" | "onPanoTransformChanged"
  > & {
    /**
     * true if cursor is hovering on a waypoint and this component will do nothing when clicked.
     * It will leave to the waypoint to handle the click event and jump to the clicked waypoint.
     * Otherwise, current component will handle the click event.
     */
    isCursorOverPlaceholder?: boolean;
  };

/** @returns a component that renders a LOD panorama image and loads and manages its depth information if present.  */
function WalkPanoWithDepth({
  pano,
  sheetForElevation,
  isSecondaryView,
  onModelClicked,
  onModelHovered,
  onModelZoomed,
  onPanoTransformChanged,
  isCursorOverPlaceholder,
}: PanoWidthDepthProps): JSX.Element {
  const [ref, setRef] = useState<Img360PanoRef | null>(null);
  const depthMap = useAppSelector(selectDepthMapForImg360(pano.iElement));

  const { projectApiClient } = useApiClientContext();

  useEffect(() => {
    if (!ref) return;
    if (!depthMap) return;

    const controller = new AbortController();
    const connection =
      ref === pano ? undefined : ref.depthsChanged.pipe(pano.depthsChanged);
    if (ref.depths === undefined) {
      loadPanoDepthData(
        ref,
        depthMap,
        projectApiClient,
        controller.signal,
      ).catch((error) => {
        console.warn(error);
      });
    }
    return () => {
      controller.abort();
      connection?.dispose();
    };
  }, [ref, depthMap, pano, projectApiClient]);

  const panoForRaycast = useMemo<PanoObject>(() => {
    if (!ref) return pano;
    if (ref === pano) {
      return pano;
    }
    return Object.assign(ref, { iElement: pano.iElement });
  }, [pano, ref]);

  useSceneContextMenuEventHandlers({ panoObject: panoForRaycast });

  const onClick = useCallback(
    (ev: ThreeEvent<MouseEvent>) => {
      if (!isCursorOverPlaceholder) {
        onModelClicked(ev, pano.iElement.id);
      }
    },
    [isCursorOverPlaceholder, onModelClicked, pano.iElement],
  );

  return (
    <PanoramaRenderer
      pano={pano}
      sheetForElevation={sheetForElevation}
      ref={setRef}
      isSecondaryView={isSecondaryView}
      onClick={onClick}
      onPointerMove={(ev) => onModelHovered(ev, pano.iElement.id)}
      onWheel={(ev) => onModelZoomed(ev, pano.iElement.id)}
      onTransformChanged={onPanoTransformChanged}
    />
  );
}

/**
 * @returns the rendering logic for panorama images inside a WalkScene
 */
export function WalkPanorama({
  pano,
  photoObject,
  placeholders,
  measurements,
  shouldShowPlaceholders,
  shouldShowAnnotations,
  annotations,
  isPickingToolActive,
  sheetForElevation,
  visible,
  isSecondaryView,
  clippingPlanes,
  highlightedElement,
  onTargetElementChanged,
  onWayPointChanged,
  onRenderedWaypointsChanged,
  onPanoTransformChanged,
  ...toolEvents
}: WalkPanoramaProps): JSX.Element | null {
  // Compute annotations section and adjusted pose
  const panoAdjustedPose = usePanoAnnotationsAdjustedPose(pano.iElement);
  const panoSection = useAppSelector(
    selectPanoAnnotationSection(pano.iElement),
  );

  const [isHoveringOnPlaceholder, setIsHoveringOnPlaceholder] = useState(false);

  const isPanoWithDepth = useAppSelector(selectIsPanoWithDepth(pano.iElement));
  assert(
    !isPanoWithDepth || pano.iElement.isRotationAccurate,
    "isRotationAccurate must be true if pano has depth info",
  );

  const camera = useThree((s) => s.camera);
  // Holobuilder markups/annotations could be closer than expected
  // and we want to avoid to clip them
  useEffect(() => {
    const { near } = camera;
    camera.near = 0.01;
    camera.updateProjectionMatrix();
    return () => {
      camera.near = near;
      camera.updateProjectionMatrix();
    };
  }, [camera]);

  // Collect annotations of the current panorama
  const generalAnnotations = useAppSelector(
    selectChildrenDepthFirst(panoSection, isIElementGenericAnnotation),
    isEqual,
  );
  const filteredGeneralAnnotations = useAppSelector(
    selectFilteredElementsWithTags(generalAnnotations, (el) =>
      selectChildDepthFirst(el, isIElementMarkup),
    ),
    isEqual,
  );

  const isAiChatAllowed = useAppSelector(selectHasFeature(Features.AiChat));
  const shouldShowBoundingBoxes = useAppSelector(
    selectShouldRenderBoundingBoxes(pano.iElement.id),
  );

  // Remove general annotations from area annotations, so that there wont be duplicate rendering of same annotations
  const areaAnnotations = useMemo(
    () =>
      annotations.filter(
        (annotation) =>
          !filteredGeneralAnnotations.some((a) => a.id === annotation.id),
      ),
    [annotations, filteredGeneralAnnotations],
  );

  const [cameraAnimation, setCameraAnimation] =
    useState<CameraAnimationProps>();

  // When the user presses goToAnnotation, either a lookAt or a setCameraView event are
  // dispatched. These events are reacted upon inside the hook below.
  useReactToSceneEvents(pano.iElement.id, camera, setCameraAnimation);

  const walls = useAppSelector(
    selectPanoMeasurementWalls(pano.iElement.id),
    isEqual,
  );

  useLookAtPhotoObject(camera, panoAdjustedPose, photoObject);

  const onPlaceholderClick = useCallback(
    (targetElement: IElementImg360, position: Vector3) => {
      onTargetElementChanged(targetElement);

      onWayPointChanged({
        targetElement,
        position,
        quaternion: camera.quaternion,
      });
    },
    [camera.quaternion, onTargetElementChanged, onWayPointChanged],
  );

  const isPanoAligned = useAppSelector(selectIsPanoAligned(pano.iElement));

  if (!visible) {
    return null;
  }

  return (
    <>
      {cameraAnimation && (
        <CameraAnimation
          {...cameraAnimation}
          onAnimationFinished={() => setCameraAnimation(undefined)}
        />
      )}
      <WalkPanoWithDepth
        pano={pano}
        sheetForElevation={sheetForElevation}
        isSecondaryView={isSecondaryView}
        {...toolEvents}
        isCursorOverPlaceholder={isHoveringOnPlaceholder}
        onPanoTransformChanged={onPanoTransformChanged}
      />
      {shouldShowPlaceholders && (
        <WalkPlaceholders
          pano={pano}
          placeholders={placeholders}
          sheetForElevation={sheetForElevation}
          clippingPlanes={clippingPlanes}
          highlightedElement={highlightedElement}
          onPlaceholderClick={onPlaceholderClick}
          onHoveringPlaceholder={setIsHoveringOnPlaceholder}
          onRenderedWaypointsChanged={onRenderedWaypointsChanged}
          shouldFadeOff
        />
      )}
      {shouldShowAnnotations && (
        <>
          {/* The annotations which are local to the panorama and they should not be collapsed */}
          <AnnotationsRenderer
            annotations={filteredGeneralAnnotations}
            worldTransform={panoAdjustedPose}
            preventCollapse
            onTargetElementChanged={onTargetElementChanged}
          />
          {isPanoAligned &&
            isPanoWithDepth && (
              // Do not show area annotations for a pano without depth info
              // Annotation that are created in the nearby area of the panorama, which needs to be collapsed if they are far away
              // Visible only if the pano has a correct rotation
              <AnnotationsRenderer
                annotations={areaAnnotations}
                worldTransform={panoAdjustedPose}
                onTargetElementChanged={onTargetElementChanged}
              />
            )}
        </>
      )}
      <MeasurementsRenderer
        measurements={measurements}
        isPickingToolActive={isPickingToolActive}
        clippingPlanes={clippingPlanes}
        pano={pano.iElement}
        showAllMeasurementsWithPano={isPanoWithDepth}
      />
      {isPanoAligned && <PinMarkerRenderer />}
      <PanoMeasureMultiWallsRenderer
        pano={pano}
        walls={walls}
        {...toolEvents}
      />

      {isAiChatAllowed && shouldShowBoundingBoxes && (
        <BoundingBoxRenderer pano={pano.iElement} />
      )}
    </>
  );
}

function useLookAtPhotoObject(
  camera: Camera,
  panoAdjustedPose: Matrix4,
  photoObject: IElementImageCommand | undefined,
): void {
  const imageCommandWorldTransform = useAppSelector(
    selectIElementWorldMatrix4(photoObject?.id),
  );

  const dispatch = useAppDispatch();

  const { isControlsLocked } = useControlsLockContext();

  useEffect(() => {
    if (photoObject && !isControlsLocked) {
      const { position } = matrix4ToAlignmentTransform(
        panoAdjustedPose.clone().multiply(imageCommandWorldTransform),
      );

      camera.quaternion.setFromRotationMatrix(
        new Matrix4().lookAt(
          camera.position,
          new Vector3().fromArray(position),
          camera.up,
        ),
      );

      camera.updateProjectionMatrix();

      dispatch(setPhotoObject());
    }
  }, [
    imageCommandWorldTransform,
    camera,
    panoAdjustedPose,
    photoObject,
    dispatch,
    isControlsLocked,
  ]);
}

/**
 * Ensures that, when the user presses goToAnnotation within the given pano, the camera
 * animates to the correct point/camera pose. This is implemented by listening to the
 * lookAt and setCameraView events from the SceneEvents context.
 *
 * @param panoId ID of the pano the event relates to
 * @param camera Current camera
 * @param setCameraAnimation Callback issued when a lookAt or setCameraView event arrives
 */
function useReactToSceneEvents(
  panoId: GUID,
  camera: Camera,
  setCameraAnimation: (animation: CameraAnimationProps | undefined) => void,
): void {
  const { lookAt, setCameraView, pendingLookAt } = useSceneEvents();
  const { getState } = useAppStore();

  // On mount, check for a pending lookAt that was stored before the walk scene was ready.
  useNonExhaustiveEffect(() => {
    if (
      pendingLookAt.target &&
      panoId === getState().selections.activeElement
    ) {
      const { target } = pendingLookAt;
      pendingLookAt.target = null;
      const quaternion = new Quaternion().setFromRotationMatrix(
        new Matrix4().lookAt(camera.position, target, camera.up),
      );
      setCameraAnimation({ quaternion });
    }
  }, []);

  useTypedEvent(lookAt, (position: Vector3) => {
    // If this event was meant on another pano, do nothing
    if (panoId !== getState().selections.activeElement) return;
    const quaternion = new Quaternion().setFromRotationMatrix(
      new Matrix4().lookAt(camera.position, position, camera.up),
    );
    setCameraAnimation({
      quaternion,
    });
  });
  useTypedEvent(setCameraView, (cameraView) => {
    // If this event was meant on another pano, do nothing
    if (panoId !== getState().selections.activeElement) return;
    setCameraAnimation({
      quaternion: cameraView.rotation,
      fov: cameraView.zoomLevel,
    });
  });
}
